import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import Autosuggest, { ChangeEvent, InputProps, RenderSuggestionParams, ShouldRenderReasons, SuggestionSelectedEventData, SuggestionsFetchRequestedParams } from 'react-autosuggest';
import { Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "redux";
import register from "../../registerServiceWorker";
import { clearSelectedItem, getDescriptionSuggestions, getItems, upsertItem } from "../../store/actions/timesheet.actions";
import { IAccountState } from "../../store/models/IAccountState";
import { IApplicationState } from "../../store/models/IApplicationState";
import { IClient } from "../../store/models/IClient";
import { IState } from "../../store/models/IState";
import { ITimesheetItem } from "../../store/models/ITimesheetItem";
import { ITimesheetItemFormState } from "../../store/models/ITimesheetItemFormState";
import { ITimesheetState } from "../../store/models/ITimesheetState";
import { UpsertType } from "../../store/models/UpsertType";
import { validationMessageContent } from "../../utils/ComponentUtils";
import { getInputClassName } from "../../utils/utils";
import ApplicationError from "../common/ApplicationError";
import { InputCssClasses } from "../common/InputCssClasses";
import MessageAlert, { MessageAlertType } from "../common/MessageAlert";
import NumberInput from "../common/NumberInput";
import Select from "../common/Select";
import TextInput from "../common/TextInput";
import { ValidationMessages } from "../common/ValidationMessages";
import { IFormStateField, OnChangeModel } from "../types/Form.types";

export interface ITimesheetEntryFormProps {
    isModal?: boolean;
    onOkButtonClick?: () => void;
    onCancelButtonClick?: () => void;
}

const TimesheetEntryForm: React.FC<ITimesheetEntryFormProps> = (props) => {
    const dispatch: Dispatch<any> = useDispatch();
    const timesheetState: ITimesheetState = useSelector((state: IState) => state.timesheetState);
    const selectedStartDate: Date = timesheetState.selectedStartDate;
    const selectedItem = timesheetState.selectedItem;
    const upsertType: string = timesheetState.selectedItem ? UpsertType.Update : UpsertType.Insert;
    const applicationState: IApplicationState = useSelector((state: IState) => state.applicationState);
    const errorMessage = applicationState?.errorMessage
    const accountState: IAccountState = useSelector((state: IState) => state.accountState);
    const [clients, setClients] = useState(applicationState.settings.clients);
    const [suggestions, setSuggestions] = useState(timesheetState?.descriptionSuggestions || []);
    const isInsert = upsertType === UpsertType.Insert;
    const okButtonRef = useRef<HTMLButtonElement>();

    // Information message after successful upsert.
    const [message, setMessage] = useState("");

    // Sets whether an upsert has been dispatched to determine whether to clear the form.
    const [upsertDispatched, setUpsertDispatched] = useState(false);
    const [upsertedItemDetails, setUpsertedItemDetails] = useState("");

    // Validation of the form.
    const defaultValidationMessage = "Please enter a valid value for all fields.";
    const [formValidationError, setFormValidationError] = useState("");
    const [descriptionError, setDescriptionError] = useState("");

    /**
     * Get the Date in the ISO format to display in the date picker control.
     * @param dateTimeString The datetime to display.
     */
    function getIsoString(dateTimeString: string): string {
        return new Date(dateTimeString).toISOString();
    }

    /**
     * Determines if the current time is before or after the hour specified in settings.
     * If it is before the date returned will be yesterday, otherwise, today will be returned.
     **/
    const getDefaultDate = useCallback((): string => {
        const currentDayHour = applicationState.settings?.thresholdHour || 0;
        const currentTime = new Date();
        const yesterday = new Date(currentTime);
        yesterday.setDate(yesterday.getDate() - 1);
        const thresholdTime = new Date(currentTime.getFullYear(), currentTime.getMonth(), currentTime.getDate(), currentDayHour, 0, 0, 0);

        const isDefaultToday = currentTime >= thresholdTime;
        const result = isDefaultToday ? currentTime : yesterday;

        return result.toISOString();
    }, [applicationState.settings]);

    let initialItemFormState: ITimesheetItemFormState = {
        id: { error: "", value: isInsert || !selectedItem ? "" : selectedItem.id, touched: false },
        userId: { error: "", value: isInsert || !selectedItem ? "" : selectedItem.userId || "", touched: false },
        date: { error: "", value: isInsert || !selectedItem ? getDefaultDate() : getIsoString(selectedItem.date.toString()), touched: false },
        timeInHours: { error: "", value: isInsert || !selectedItem ? 0.0 : selectedItem.timeInHours, touched: false },
        description: { error: "", value: isInsert || !selectedItem ? "" : selectedItem.description, touched: false },
        client: { error: "", value: isInsert || !selectedItem ? "" : selectedItem.client || "", touched: false },
        task: { error: "", value: isInsert || !selectedItem ? "" : selectedItem.task || "", touched: false },
        partitionKey: { error: "", value: isInsert || !selectedItem ? "" : selectedItem.partitionKey || "", touched: false },
        rowKey: { error: "", value: isInsert || !selectedItem ? "" : selectedItem.rowKey || "", touched: false },
        timestamp: { error: "", value: isInsert || !selectedItem || !selectedItem.timestamp ? new Date().toISOString() : getIsoString(selectedItem.timestamp.toString()), touched: false },
        eTag: { error: "", value: isInsert || !selectedItem ? "" : selectedItem.eTag || "", touched: false },
    };

    // Form values.
    const [itemFormState, setItemFormState] = useState(initialItemFormState);

    // When the settings are loaded we need to use the threshold time to determine the date to display in the form.
    useEffect(() => {
        if (isInsert && !itemFormState.date.touched) {
            let currentItemState = itemFormState;
            currentItemState.date = { error: "", value: getDefaultDate(), touched: false };
            setItemFormState(currentItemState);
        }
    }, [applicationState.settings, getDefaultDate, isInsert, itemFormState])

    // The following is used to be allow to get the latest itemFormState values from within a callback function.
    // E.g. in the onSuggestionInputFieldChange it needs to reference the current state and update it and there
    // was a bug where it was referencing only the initial state values.
    const itemFormStateRef = useRef<ITimesheetItemFormState>();
    itemFormStateRef.current = itemFormState;

    /**
     * The input properties for the Description autosuggestion input field. 
     **/
    const defaultSuggestionInputProperties: InputProps<string> = {
        placeholder: "Description...",
        value: itemFormState.description.value,
        onChange: onSuggestionInputFieldChange,
        className: getInputClassName(false, "", ""),
        id: "input_description_autosuggest"
    };

    const [suggestionInputProperties, setSuggestionInputProperties] = useState(defaultSuggestionInputProperties);

    useEffect(() => {
        if (!props.isModal) {
            // Ensure that any selected item from the Timesheet list is no longer selected as we need to enter
            // a new time entry here.
            dispatch(clearSelectedItem());
        }
    }, [props.isModal, dispatch]);

    useEffect(() => {
        setClients(applicationState?.settings?.clients);
    }, [applicationState, applicationState.settings, applicationState.settings.clients]);

    useEffect(() => {
        // Ensure the current list of timesheet items for the current month has been loaded.
        dispatch(getItems(selectedStartDate));

        // Get initial suggestions.
        dispatch(getDescriptionSuggestions());
    }, [dispatch, selectedStartDate]);

    useEffect(() => {
        setSuggestions(timesheetState?.descriptionSuggestions);
    }, [timesheetState, timesheetState.descriptionSuggestions]);

    /**
     * Clearing the state as the modal is being reused so avoids saved state from a previous display.
     */
    const clearState = useCallback((isClearUpsertMessage: boolean = false): void => {
        setSuggestionInputProperties({
            ...defaultSuggestionInputProperties,
            value: initialItemFormState.description.value,
            className: getInputClassName(false, "", "")
        });
        setItemFormState(initialItemFormState);
        setFormValidationError("");
        setDescriptionError("");
        if (isClearUpsertMessage) {
            setMessage("");
        }
    }, [setSuggestionInputProperties, defaultSuggestionInputProperties, initialItemFormState, setItemFormState, setFormValidationError, setDescriptionError, setMessage]);

    useEffect(() => {
        if (upsertDispatched && errorMessage.length === 0) {
            // Upsert successful so clear the form.
            setMessage(`Time entry added: '${upsertedItemDetails}'`);
            clearState();

            // Reset these items
            setUpsertDispatched(false);
            setUpsertedItemDetails("");
        }
    }, [errorMessage, upsertDispatched, clearState, upsertedItemDetails])

    /**
     * Checks that all the required fields have been completed as expected.
     **/
    function isFormValid(model?: OnChangeModel): boolean {
        // Checks the field's onChange error first.
        let isValid = model ? model.error.length === 0 : true;

        if (model) {
            // Clear the upsert message as the user is entering new details.
            setMessage("");
        } else if (isValid) {
            // Check all the required fields to see if valid.
            var requiredItemStates: IFormStateField<any>[] =
                [
                    itemFormState.client,
                    itemFormState.date,
                    itemFormState.timeInHours,
                    itemFormState.description
                ];

            for (var i = 0; i < requiredItemStates.length; i++) {
                const itemState = requiredItemStates[i];

                if (typeof itemState.value === "string") {
                    isValid = itemState.value?.length > 0;
                } else if (typeof itemState.value === "number") {
                    isValid = itemState.value >= 0;
                }

                if (!isValid) {
                    break;
                }
            }
        }

        setFormValidationError(isValid ? "" : defaultValidationMessage)

        return isValid;
    }

    /**
     * Upsert the inserted/updated employee and close the dialog.
     */
    function okButtonClick(): void {
        setMessage("");

        if (isFormValid()) {
            let item: ITimesheetItem = {
                id: itemFormState.id.value,
                userId: accountState.account.email,
                client: itemFormState.client.value,
                task: itemFormState.task.value,
                date: new Date(itemFormState.date.value),
                timeInHours: itemFormState.timeInHours.value,
                description: itemFormState.description.value,
                timestamp: itemFormState.timestamp ? new Date(itemFormState.timestamp.value) : undefined,
                eTag: itemFormState.eTag.value,
                partitionKey: itemFormState.partitionKey.value,
                rowKey: itemFormState.rowKey.value
            };

            dispatch(upsertItem(item));

            if (props.onOkButtonClick) {
                props.onOkButtonClick();
            }

            setUpsertDispatched(true);
            setUpsertedItemDetails(item.description);
        }
    }

    /**
     * When the cancel button is clicked then clear the form state.
     * Call the callback function if specified in properties.
     **/
    function cancelButtonClick(): void {
        clearState(true);

        if (props.onCancelButtonClick) {
            props.onCancelButtonClick();
        }
    }

    /**
     * Update the state of the control in the form state object.
     * @param model The input element that has changed will pass the model to be updated.
     */
    function onFormFieldChange(model: OnChangeModel): void {
        const newState: ITimesheetItemFormState = {
            ...itemFormState,
            [model.field]: { error: model.error, value: model.value, touched: model.touched }
        };
        setItemFormState(newState);
        isFormValid(model);
    }

    /**
     * Determines whether to display the form for a modal or just a page.
     **/
    function getFormContent(): JSX.Element {
        if (props.isModal) {
            return (
                <Fragment>
                    <Modal.Body>
                        {getEntryForm()}
                    </Modal.Body>
                    <Modal.Footer>
                        {getButtons()}
                    </Modal.Footer>
                </Fragment>
            );
        }

        return (
            <Fragment>
                <h1 className="d-none d-sm-none d-md-block">Enter Time</h1>
                <ApplicationError />
                <MessageAlert type={MessageAlertType.Information} message={message} />
                {getEntryForm()}
                <div className="button-container float-right">
                    {getButtons()}
                </div>
            </Fragment>
        );
    }

    /**
     * Display the buttons.
     **/
    function getButtons(): JSX.Element {
        return (
            <Fragment>
                <Button variant="primary" onClick={okButtonClick} ref={(e: HTMLButtonElement) => { register(); okButtonRef.current = e; }}>OK</Button>
                <Button variant="secondary" onClick={cancelButtonClick}>Cancel</Button>
            </Fragment>
        );        
    }

    /**
     * Retrieve just the client names to display in a drop down list.
     **/
    function getClients(): string[] {
        var result = clients.map((client: IClient, index: number, array: IClient[]) => {
            return client.name;
        });

        return result;
    }

    /**
     * Allows calling out to an API to get different results or limit the existing results based on the user's 
     * input in the description field.
     * 
     * See Autosuggest documentation here: https://github.com/moroshko/react-autosuggest.
     * @param request
     */
    function onSuggestionsFetchRequested(request: SuggestionsFetchRequestedParams): void {
        if (request.value.length > 0) {
            // Filters the list as the user types to restrict to items that match their text.
            const descriptionSuggestions = suggestions.filter(s => s.toLocaleLowerCase().indexOf(request.value.toLocaleLowerCase()) >= 0);

            setSuggestions(descriptionSuggestions);
        }
        else {
            setSuggestions(timesheetState.descriptionSuggestions);
        }
    }

    /**
     * Clear the suggestions displayed.
     **/
    function onSuggestionsClearRequested(): void {
        // TODO: Decided if we need to do anything here.
    }

    /**
     * Sets the description form state to reflect the value the user selected from the autosuggest list.
     * @param event
     * @param data
     */
    function onSuggestionSelected(event: React.FormEvent<any>, data: SuggestionSelectedEventData<string>): void {
        setDescriptionFormState(data.suggestionValue);

        // Focus the OK button.
        okButtonRef.current?.focus();
    }

    /**
     * Used to modify the autosuggestion list output if required.
     * @param suggestion
     * @param params
     */
    function renderSuggestion(suggestion: string, params: RenderSuggestionParams): React.ReactNode {
        return (
            <div>{suggestion}</div>
        );
    }

    /**
     * Updates the form state for the description field.
     * @param value
     */
    function setDescriptionFormState(value: string) {
        const touched = true;
        const fieldName = "description";
        const errorMessage = value || value.trim().length > 0 ? "" : ValidationMessages.ValueIsRequired;
        setDescriptionError(errorMessage);

        const currentState = itemFormStateRef.current || initialItemFormState;
        const newState: ITimesheetItemFormState = {
            ...currentState,
            [fieldName]: { error: errorMessage, value: value, touched: touched }
        };
        setItemFormState(newState);

        const model: OnChangeModel = {
            error: errorMessage,
            value: value,
            touched: touched,
            field: fieldName
        };

        isFormValid(model);
    }

    /**
     * When the autosuggestion field is edited save the value back to the form state.
     * @param event
     * @param params
     */
    function onSuggestionInputFieldChange(event: React.FormEvent<HTMLElement>, params: ChangeEvent): void {
        if (params.method === "type") {
            setDescriptionFormState(params.newValue);
        }

        // Update the validity css classes as appropriate.
        const validityCssClass = params.newValue.length > 0 ? InputCssClasses.ValidCssClass : InputCssClasses.InvalidCssClass;
        let inputProperties = {
            ...suggestionInputProperties,
            className: getInputClassName(true, "", validityCssClass),
            value: params.newValue
        };
        setSuggestionInputProperties(inputProperties);
    }

    /**
     * Determines when the autosuggest list should be displayed.
     * @param value The input field value.
     * @param reason The different events that fire this method.
     */
    function shouldRenderSuggestions(value: string, reason: ShouldRenderReasons): boolean {
        return value.length >= 0;
    }

    /**
     * Determines whether to display a warning alert to inform the user that form field values are invalid.
     **/
    function getValidationError(): JSX.Element | null {
        if (formValidationError.length === 0) {
            return null;
        }

        return (
            <MessageAlert type={MessageAlertType.Warning} message={formValidationError} />
        );
    }

    /**
     * Displays the form for entering timesheet items with a validation message.
     **/
    function getEntryForm(): JSX.Element {
        return (
            <div className="entry-form">
                {getValidationError()}
                <div className="form-row">
                    <div className="form-group col-6">
                        <TextInput
                            id="input_date"
                            type="datepicker"
                            value={itemFormState.date.value}
                            field="date"
                            onChange={onFormFieldChange}
                            required={true}
                            label="Date"
                            placeholder="Date"
                            isClearable={false}
                            touched={itemFormState.date.touched}
                        />
                    </div>
                    <div className="form-group col-6">
                        <NumberInput
                            id="input_hours"
                            value={itemFormState.timeInHours.value}
                            field="timeInHours"
                            onChange={onFormFieldChange}
                            required={true}
                            label="Time (hours)"
                            min={0}
                            autofocus={true}
                            touched={itemFormState.timeInHours.touched}
                        />
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group col-12">
                        <Select
                            id="input_client"
                            value={itemFormState.client.value}
                            field="client"
                            onChange={onFormFieldChange}
                            required={true}
                            label="Client"
                            options={getClients()}
                            touched={itemFormState.client.touched}
                        />
                    </div>
                </div>                
                <div className="form-row">
                    <div className="form-group col-12">
                        <label htmlFor="input_description_autosuggest">Description<small> (required)</small></label>
                        <Autosuggest
                            id="input_description_autosuggest"
                            suggestions={suggestions}
                            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                            onSuggestionsClearRequested={onSuggestionsClearRequested}
                            onSuggestionSelected={onSuggestionSelected}
                            getSuggestionValue={suggestion => suggestion}
                            renderSuggestion={renderSuggestion}
                            inputProps={suggestionInputProperties}
                            shouldRenderSuggestions={shouldRenderSuggestions}
                            alwaysRenderSuggestions={true}
                            focusInputOnSuggestionClick={false}
                        />
                        {validationMessageContent(descriptionError)}
                    </div>
                </div>    
                <div className="form-row">
                    <div className="form-group col-12">
                        <TextInput
                            id="input_task"
                            type="text"
                            value={itemFormState.task.value}
                            field="task"
                            onChange={onFormFieldChange}
                            required={false}
                            label="Task"
                            placeholder="Task"
                            isClearable={false}
                            touched={itemFormState.task.touched}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        getFormContent()
    );
}

export default TimesheetEntryForm;